import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '../api/client';
import { Badge, Button, EmptyState, Skeleton, cn } from './primitives';
import { DialogShell } from './confirm-dialog';
import { formatRelative } from '../lib/format';
import { t } from '../../i18n';

/**
 * The bell's contents.
 *
 * Notifications carry a `deepLink` written for the mobile apps
 * (`kinshipos://bills/…`), so a link is translated to a web route here rather
 * than handed to the browser — an unhandled custom scheme does nothing at all,
 * which is indistinguishable from a broken notification.
 */

interface NotificationRow {
  id: string;
  title: string;
  body: string;
  category: string;
  priority: string;
  deepLink: string | null;
  readAt: string | null;
  createdAt: string;
}

/** Categories that should stand out in a list of forty. */
const URGENT = new Set(['sos', 'security', 'platform']);

/**
 * `kinshipos://bills/01a0…` → `/bills`.
 *
 * The admin panel has no per-record routes for most of these, so the host is
 * the useful part and the id is dropped. Returning null means "not somewhere
 * this app can go", and the row simply is not clickable.
 */
export function webRouteFor(deepLink: string | null): string | null {
  if (!deepLink) return null;

  const path = deepLink.startsWith('kinshipos://')
    ? deepLink.slice('kinshipos://'.length)
    : deepLink.startsWith('/')
      ? deepLink.slice(1)
      : null;

  if (path === null) return null;

  const [head] = path.split(/[/?#]/);
  const ROUTES: Record<string, string> = {
    home: '/dashboard',
    dashboard: '/dashboard',
    bills: '/bills',
    invoices: '/bills',
    payments: '/payments',
    complaints: '/complaints',
    visitors: '/visitors',
    amenities: '/amenities',
    bookings: '/amenities?tab=bookings',
    announcements: '/announcements',
    documents: '/documents',
    members: '/members',
    admin: '/members',
    helpers: '/helpers',
  };

  return ROUTES[head ?? ''] ?? null;
}

export function NotificationsPanel({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const notifications = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => api.getPage<NotificationRow>('/api/v1/notifications', { query: { limit: 30 } }),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/notifications/${id}/read`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['sidebar-counts'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/api/v1/notifications/read-all', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['sidebar-counts'] });
    },
  });

  const rows = notifications.data?.items ?? [];
  const unread = rows.filter((row) => !row.readAt).length;

  const openRow = (row: NotificationRow): void => {
    if (!row.readAt) markRead.mutate(row.id);

    const route = webRouteFor(row.deepLink);
    if (route) {
      onOpenChange(false);
      navigate(route);
    }
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={t('Notifications')}
      description={
        unread > 0 ? `${unread} unread` : t('Everything here has been read.')
      }
      className="max-w-lg"
      footer={
        <Button
          variant="secondary"
          onClick={() => markAllRead.mutate()}
          loading={markAllRead.isPending}
          disabled={unread === 0}
        >
          <CheckCheck className="size-4" aria-hidden />{' '}{t('Mark all as read')}
        </Button>
      }
    >
      {notifications.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Bell className="size-5" />}
          title={t('Nothing yet')}
          description={t('Approvals, complaints and payments show up here as they happen.')}
        />
      ) : (
        <ul className="divide-y divide-outline-soft">
          {rows.map((row) => {
            const route = webRouteFor(row.deepLink);
            const Element = route ? 'button' : 'div';

            return (
              <li key={row.id}>
                <Element
                  {...(route
                    ? {
                        type: 'button' as const,
                        onClick: () => openRow(row),
                        'aria-label': `${row.title}. Open.`,
                      }
                    : {})}
                  className={cn(
                    'flex w-full gap-3 py-3 text-left',
                    route &&
                      'rounded-sk-sm px-1 -mx-1 hover:bg-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/50',
                  )}
                >
                  {/* An unread dot, plus the word "New" in the row — colour on
                      its own cannot carry the state (WCAG 1.4.1). */}
                  <span
                    aria-hidden
                    className={cn(
                      'mt-1.5 size-2 shrink-0 rounded-full',
                      row.readAt ? 'bg-transparent' : 'bg-indigo',
                    )}
                  />

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-[13px] font-semibold text-ink-1">{row.title}</span>
                      {!row.readAt ? <Badge tone="indigo">{t('New')}</Badge> : null}
                      {URGENT.has(row.category) ? (
                        <Badge tone="danger">{row.category}</Badge>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block text-[13px] text-ink-2">{row.body}</span>
                    <span className="mt-0.5 block text-2xs text-ink-3">
                      {formatRelative(row.createdAt)}
                    </span>
                  </span>
                </Element>
              </li>
            );
          })}
        </ul>
      )}
    </DialogShell>
  );
}
